import * as React from "react"
import { Head, useForm, usePage } from "@inertiajs/react"
import { Play, Volume2, VolumeX } from "lucide-react"
import { AdminShell } from "@/components/AdminShell"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Select } from "@/components/ui/select"
import { previewSound } from "@/lib/sound"
import type { PageProps, SoundSettings } from "@/types/inertia"

const TITLE = "Controls"
const DESCRIPTION = "Runtime site settings: interface sound, on/off and per-event."

type Option = { id: string; label: string }

// Everything here takes effect immediately and everywhere — hosted decks and the
// public sample read these at injection time, so a change reaches audits sent
// months ago with no re-upload.
export default function AdminControls({
  settings,
  events,
  library,
}: {
  settings: SoundSettings
  events: Option[]
  library: Option[]
}) {
  const page = usePage<PageProps>()
  const form = useForm({
    sound_enabled: settings.enabled,
    sound_voices: { ...settings.voices } as Record<string, string>,
  })

  // Auditioning bypasses both the kill switch and any local mute — you have to
  // be able to hear a sound while deciding whether to turn it on. The click is
  // itself the gesture that unlocks audio.
  function audition(soundId: string) {
    if (soundId !== "none") previewSound(soundId)
  }

  function choose(eventId: string, soundId: string) {
    form.setData("sound_voices", { ...form.data.sound_voices, [eventId]: soundId })
    audition(soundId)
  }

  function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    // transform() returns void in Inertia's React useForm, so it cannot be
    // chained — wrap the payload first, then submit.
    form.transform((data) => ({ site_setting: data }))
    form.patch("/admin/controls", { preserveScroll: true })
  }

  const off = !form.data.sound_enabled

  return (
    <>
      <Head title={TITLE}>
        <meta name="description" content={DESCRIPTION} />
        <meta property="og:title" content={TITLE} />
        <meta property="og:description" content={DESCRIPTION} />
      </Head>

      <AdminShell>
        <div className="border-b border-hairline pb-6">
          <h1>Controls</h1>
          <p className="mt-1">
            Runtime settings. Changes apply everywhere the moment you save — the
            site, every hosted audit including ones already sent, and the public
            sample.
          </p>
        </div>

        {page.props.flash?.notice && (
          <p className="mt-6 text-sm text-accent">{page.props.flash.notice}</p>
        )}
        {page.props.errors?.sound_voices && (
          <p className="mt-6 text-sm text-danger">{page.props.errors.sound_voices}</p>
        )}

        <form onSubmit={submit} className="mt-6 max-w-2xl">
          <section className="rounded-md border border-hairline bg-page p-5">
            <div className="flex items-start gap-3">
              <Checkbox
                id="sound_enabled"
                className="mt-1"
                checked={form.data.sound_enabled}
                onChange={(e) => form.setData("sound_enabled", e.target.checked)}
              />
              <div className="min-w-0">
                <label htmlFor="sound_enabled" className="flex items-center gap-2">
                  {off ? (
                    <VolumeX aria-hidden="true" className="h-4 w-4 text-ink-muted" />
                  ) : (
                    <Volume2 aria-hidden="true" className="h-4 w-4 text-ink-muted" />
                  )}
                  Sounds
                </label>
                <p className="mt-1 text-sm text-ink-muted">
                  {off
                    ? "Off. Nothing makes a sound anywhere, and visitors are not shown a mute control. Anyone who had already chosen keeps that choice for when you switch back on."
                    : "On. Each visitor still gets their own mute, defaulting to silent for anyone who asks their system for reduced motion."}
                </p>
              </div>
            </div>
          </section>

          <section
            // Not disabled when sound is off: choosing and auditioning sounds
            // while it is switched off is exactly how you decide to switch it
            // back on. Dimmed to show it is not currently reaching anyone.
            className={
              off
                ? "mt-6 rounded-md border border-hairline bg-page p-5 opacity-60"
                : "mt-6 rounded-md border border-hairline bg-page p-5"
            }
          >
            <h2 className="text-base">Sound per event</h2>
            <p className="mt-1 text-sm text-ink-muted">
              Picking a sound plays it. Pitch and level stay tied to the event, so
              a reveal keeps receding and a slide change keeps walking the deck.
            </p>

            <div className="mt-5 flex flex-col gap-4">
              {events.map((event) => {
                const value = form.data.sound_voices[event.id] ?? "wood"
                return (
                  <div
                    key={event.id}
                    className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3"
                  >
                    <label htmlFor={`voice_${event.id}`} className="sm:w-56 sm:shrink-0">
                      {event.label}
                    </label>
                    <div className="flex flex-1 items-center gap-2">
                      <Select
                        id={`voice_${event.id}`}
                        className="flex-1"
                        value={value}
                        onChange={(e) => choose(event.id, e.target.value)}
                      >
                        {library.map((sound) => (
                          <option key={sound.id} value={sound.id}>
                            {sound.label}
                          </option>
                        ))}
                      </Select>
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        aria-label={`Play ${event.label} sound`}
                        title="Play"
                        disabled={value === "none"}
                        onClick={() => audition(value)}
                      >
                        <Play aria-hidden="true" className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )
              })}
            </div>
          </section>

          <div className="mt-6 flex items-center gap-3">
            <Button type="submit" disabled={form.processing}>
              {form.processing ? "Saving…" : "Save controls"}
            </Button>
            {form.isDirty && <span className="text-sm text-ink-muted">Unsaved changes</span>}
          </div>
        </form>
      </AdminShell>
    </>
  )
}
